import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { type IWidgetConfigComponentProps } from '@framework/engine/schema/directives/IWidgetDirectiveType.ts';
import { useFieldArrayOf } from '@framework/form/form/relations/useFieldsArrayOf.ts';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { $LabeledSpaceCompact } from '@framework/ui/components/space/SpaceCompact.tsx';
import { Button, Divider, Tooltip, Typography } from 'antd';
import { type ICustomProperty } from './ICustomPropertiesDirective.ts';

export interface ICustomPropertiesDirectiveConfigComponentProps
  extends IWidgetConfigComponentProps {}

/**
 * Configurazione delle coppie chiave/valore personalizzate del widget.
 * Lista dinamica: si possono aggiungere e rimuovere coppie. Il codice è
 * obbligatorio, il valore è facoltativo; la configurazione si azzera quando
 * tutte le coppie sono vuote.
 */
export function CustomPropertiesDirectiveConfigComponent(
  props: ICustomPropertiesDirectiveConfigComponentProps,
) {
  const { name } = props;
  const { fields, append, remove } = useFieldArrayOf<ICustomProperty>({
    name: `${name}.pairs`,
  });

  return (
    <>
      <Divider orientation="left" orientationMargin={0}>
        Proprietà personalizzate
      </Divider>
      <Typography.Paragraph type="secondary">
        Coppie codice/valore aggiuntive da associare al campo.
      </Typography.Paragraph>
      {fields.length > 0 && (
        <Typography.Paragraph type="secondary">
          Il codice deve iniziare con una lettera e può contenere lettere,
          numeri, - e _.
        </Typography.Paragraph>
      )}
      {fields.map((field, index) => (
        <$LabeledSpaceCompact block size="large" key={field.id}>
          <FormItem name={`${name}.pairs.${index}.key`} label="Codice">
            <TextInput
              name={`${name}.pairs.${index}.key`}
              placeholder="es. codice"
              allowClear
            />
          </FormItem>
          <FormItem name={`${name}.pairs.${index}.value`} label="Valore">
            <TextInput
              name={`${name}.pairs.${index}.value`}
              placeholder="es. valore"
              allowClear
            />
          </FormItem>
          <Tooltip title="Rimuovi proprietà">
            <Button
              danger
              aria-label="Rimuovi proprietà"
              icon={<DeleteOutlined />}
              onClick={() => remove(index)}
            />
          </Tooltip>
        </$LabeledSpaceCompact>
      ))}
      <Button
        icon={<PlusOutlined aria-hidden="true" />}
        block={fields.length === 0}
        size={fields.length === 0 ? 'large' : 'middle'}
        onClick={() => append({ key: '', value: '' })}
      >
        Aggiungi proprietà
      </Button>
    </>
  );
}
